import { useQueryClient } from '@tanstack/react-query';
import { UserKeys } from '../../../api/users-api';
import { MobileDevice } from '../../../../types';
import {
  isAbleToSeeUIElement,
  ToolsRoles,
} from '../../../utils/roles/client-roles';
import { Dispatch, SetStateAction } from 'react';
import SharedToolbar from '../../../shared/shared-toolbar-latest';
import SharedGridStateIconButton from '../../../shared/shared-grid-state-icon-button-latest';
import { ModuleList } from '../../../shared/analytics/module-list';

import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Check from '@mui/icons-material/Check';
import Delete from '@mui/icons-material/Delete';
import DeleteSweep from '@mui/icons-material/DeleteSweep';
import DoNotDisturb from '@mui/icons-material/DoNotDisturb';
import FolderDelete from '@mui/icons-material/FolderDelete';
import Refresh from '@mui/icons-material/Refresh';

interface MobileDevicesIndexedToolbarProps {
  selectedDevices: MobileDevice[];
  toolsRoles: string[];
  setOpenDeleteDevice: Dispatch<SetStateAction<boolean>>;
  setOpenApproveDevice: Dispatch<SetStateAction<boolean>>;
  setOpenBlockDevice: Dispatch<SetStateAction<boolean>>;
  setOpenWipeDevice: Dispatch<SetStateAction<boolean>>;
  setOpenWipeAccountDevice: Dispatch<SetStateAction<boolean>>;
}

export default function MobileDevicesIndexedToolbar({
  selectedDevices,
  toolsRoles,
  setOpenDeleteDevice,
  setOpenApproveDevice,
  setOpenBlockDevice,
  setOpenWipeDevice,
  setOpenWipeAccountDevice,
}: MobileDevicesIndexedToolbarProps) {
  const queryClient = useQueryClient();

  // All
  const hasNoLength = selectedDevices.length === 0;

  // Approve
  const hasOnlyPendingOrBlockedDevices = selectedDevices.every(
    (device) => device.status === 'PENDING' || device.status === 'BLOCKED'
  );
  const hasApprovePermission = isAbleToSeeUIElement(
    toolsRoles,
    ToolsRoles.CanApproveMobile.value
  );

  // Block
  const hasOnlyPendingOrApprovedDevices = selectedDevices.every(
    (device) => device.status === 'PENDING' || device.status === 'APPROVED'
  );
  const hasBlockPermission = isAbleToSeeUIElement(
    toolsRoles,
    ToolsRoles.CanBlockMobile.value
  );

  // Remote Wipe
  const hasOnlyPendingOrApprovedOrBlockedCompanyDevices = selectedDevices.every(
    (device) =>
      (device.status === 'PENDING' ||
        device.status === 'APPROVED' ||
        device.status === 'BLOCKED') &&
      device.isForCompanyUseOnly
  );
  const hasWipePermission = isAbleToSeeUIElement(
    toolsRoles,
    ToolsRoles.CanRemoteWipeMobile.value
  );

  // Remote Account Wipe
  const hasOnlyPendingOrApprovedOrBlockedDevices = selectedDevices.every(
    (device) =>
      device.status === 'PENDING' ||
      device.status === 'APPROVED' ||
      device.status === 'BLOCKED'
  );
  const hasRemoteAccountWipePermission = isAbleToSeeUIElement(
    toolsRoles,
    ToolsRoles.CanRemoteWipeAccountMobile.value
  );

  // Delete
  const hasDeletePermission = isAbleToSeeUIElement(
    toolsRoles,
    ToolsRoles.CanDeleteMobile.value
  );

  return (
    <SharedToolbar
      quickFilterPlaceholder="Search By Name..."
      quickFilterDebounceMs={300}
    >
      <Tooltip arrow title="Approve">
        <span>
          <IconButton
            onClick={() => {
              setOpenApproveDevice(true);
            }}
            disabled={
              hasNoLength ||
              !hasOnlyPendingOrBlockedDevices ||
              !hasApprovePermission
            }
          >
            <Check />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip arrow title="Block">
        <span>
          <IconButton
            onClick={() => {
              setOpenBlockDevice(true);
            }}
            disabled={
              hasNoLength ||
              !hasOnlyPendingOrApprovedDevices ||
              !hasBlockPermission
            }
          >
            <DoNotDisturb />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip arrow title="Remote Wipe">
        <span>
          <IconButton
            onClick={() => {
              setOpenWipeDevice(true);
            }}
            disabled={
              hasNoLength ||
              !hasOnlyPendingOrApprovedOrBlockedCompanyDevices ||
              !hasWipePermission
            }
          >
            <DeleteSweep />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip arrow title="Remote Account Wipe">
        <span>
          <IconButton
            onClick={() => {
              setOpenWipeAccountDevice(true);
            }}
            disabled={
              hasNoLength ||
              !hasOnlyPendingOrApprovedOrBlockedDevices ||
              !hasRemoteAccountWipePermission
            }
          >
            <FolderDelete />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip arrow title="Delete">
        <span>
          <IconButton
            onClick={() => {
              setOpenDeleteDevice(true);
            }}
            disabled={hasNoLength || !hasDeletePermission}
          >
            <Delete />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip arrow title="Refresh">
        <IconButton
          onClick={() => {
            void queryClient.invalidateQueries({
              queryKey: [UserKeys.MobileDevicesIndexedPage],
            });
          }}
        >
          <Refresh />
        </IconButton>
      </Tooltip>
      <SharedGridStateIconButton
        gridStateKey="mobileManagementIndexedGridStates"
        module={ModuleList.modules.devices.name}
        section={ModuleList.modules.devices.sections.mobileManagement}
      />
    </SharedToolbar>
  );
}
